import React, { useState } from 'react';
import cx from 'classnames';
import { get } from 'lodash';
import PropTypes from 'prop-types';

import propertyPlaceholderImg from '../../../../../assets/property_img_placeholder_thumb.png';
import Button from '../../../../../components/Button';
import IconLabel from '../../../../../components/IconLabel';
import colors from '../../../../../constants/colors';
import getPropertyTypeIcon from '../../../../../helpers/get-property-type-icon';
import getPropertyTypeValue from '../../../../../helpers/getPropertyTypeValue';
import BathsThickIcon from '../../../../../icons/BathsThick';
import BedsThickIcon from '../../../../../icons/BedsThick';
import DogHeadIcon from '../../../../../icons/DogHead';
import SquareFeetThickIcon from '../../../../../icons/SquareFeetThick';
import { useConfig } from '../../../../../providers/ConfigProvider';
import getListingFullAddress from '../../../../../services/utilities/getListingFullAddress';

import styles from './EmbedPropertyListItem.module.scss';

const EmbedPropertyListItem = ({ property }) => {
  const { PUBLIC_LISTING_URL } = useConfig();
  const [imageIndex, setImageIndex] = useState(0);
  const openPublicUrl = () =>
    window.open(`${PUBLIC_LISTING_URL}${property.id}`, '_blank');

  const photo =
    get(property, 'photos.length', 0) > 0
      ? {
          url: [
            property.photos[0].picture_midsize,
            property.photos[0].picture_full,
            property.photos[0].picture,
          ],
          rotation: property.photos[0].rotation,
        }
      : { url: propertyPlaceholderImg, rotation: 0 };

  const imageUrl = Array.isArray(photo.url) ? photo.url[imageIndex] : photo.url;
  const imageRotation = photo.rotation;

  const bedrooms = get(property, 'details.property_details.bedrooms', 0) || 0;
  const baths = get(property, 'details.property_details.bathrooms', 0) || 0;
  const squareFeet = get(
    property,
    'details.property_details.square_feet',
    null,
  );
  const propertyType =
    getPropertyTypeValue(
      get(property, 'details.property_details.property_type', ''),
    ) || '-';
  const propertyIcon =
    getPropertyTypeIcon(
      get(property, 'details.property_details.property_type', ''),
    ) || null;
  const pets = get(property, 'details.lease_details.pets_allowed', false);

  return (
    <div className={styles.card}>
      <div className={styles.imageContainer}>
        <img
          className={styles.image}
          src={imageUrl || propertyPlaceholderImg}
          style={{ transform: `rotate(${imageRotation || 0}deg)` }}
          onError={() => {
            if (Array.isArray(photo) && photo.length > 0) {
              setImageIndex(imageIndex + 1);
            }
          }}
          onClick={openPublicUrl}
        />
      </div>
      <div className={styles.dataContainer}>
        <div className={styles.propertyInfo}>
          <div className={styles.titleContainer}>
            <h2 className={styles.title} onClick={openPublicUrl}>
              {property.title}
            </h2>
            <address className={styles.address}>
              {getListingFullAddress(property)}
            </address>
          </div>
          <div className={styles.priceContainer}>
            <span className={styles.amount}>${property.rent_amount}</span>
            <span>PER MONTH</span>
          </div>
        </div>
        <div className={styles.propertyData}>
          <IconLabel
            label={`${bedrooms} Bedroom${bedrooms !== 1 ? 's' : ''}`}
            icon={BedsThickIcon}
            className={styles.iconWrapper}
            labelClassName={styles.label}
            iconProps={{
              color: colors.darkBlueGrey,
              className: styles.icon,
              height: 48,
              width: 48,
            }}
          />
          <IconLabel
            label={`${baths} Bath${baths !== 1 ? 's' : ''}`}
            icon={BathsThickIcon}
            className={styles.iconWrapper}
            labelClassName={styles.label}
            iconProps={{
              color: colors.darkBlueGrey,
              className: styles.icon,
              height: 48,
              width: 48,
            }}
          />
          {propertyIcon && (
            <IconLabel
              label={propertyType}
              icon={propertyIcon}
              className={styles.iconWrapper}
              labelClassName={styles.label}
              iconProps={{
                color: colors.darkBlueGrey,
                className: styles.icon,
                height: 48,
                width: 48,
              }}
            />
          )}
          {!!squareFeet && (
            <IconLabel
              label={`${squareFeet} sq ft.`}
              icon={SquareFeetThickIcon}
              className={styles.iconWrapper}
              labelClassName={styles.label}
              iconProps={{
                color: colors.darkBlueGrey,
                className: styles.icon,
                height: 48,
                width: 48,
              }}
            />
          )}
          {pets && pets.length > 0 && (
            <IconLabel
              label={
                pets.indexOf('NO_PETS') === -1
                  ? 'Pets Allowed'
                  : 'No Pets Allowed'
              }
              icon={DogHeadIcon}
              className={cx(styles.iconWrapper, styles.dog)}
              labelClassName={styles.label}
              iconProps={{
                color: colors.darkBlueGrey,
                className: styles.icon,
                height: 48,
                width: 48,
              }}
            />
          )}
        </div>
        <div className={styles.propertyListing}>
          <span className={styles.learnMore}>
            View the listing to learn more and apply to the property.
          </span>
          <Button
            href={`${PUBLIC_LISTING_URL}${property.id}`}
            target="_blank"
            className={styles.btn}
          >
            View Listing
          </Button>
        </div>
      </div>
    </div>
  );
};

EmbedPropertyListItem.propTypes = {
  property: PropTypes.object,
};

export default EmbedPropertyListItem;
